import { useState, useEffect } from 'preact/hooks'
import { calendarEventsApi } from '@/api/calendarEvents'
import type { CalendarEventViewDto } from '@/types/calendarEvent'
import { useToast } from '@/hooks/useToast'

interface UseCalendarEventsResult {
  events: CalendarEventViewDto[]
  setEvents: (events: CalendarEventViewDto[] | ((prev: CalendarEventViewDto[]) => CalendarEventViewDto[])) => void
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useCalendarEvents(workspaceId: string, campaignId?: string, webId?: string): UseCalendarEventsResult {
  const [events, setEvents] = useState<CalendarEventViewDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { showError, showErrorUnless422 } = useToast()

  const fetchEvents = async () => {
    setLoading(true)
    setError(null)

    try {
      const response = webId
        ? await calendarEventsApi.getWebEvents(workspaceId, webId)
        : campaignId
        ? await calendarEventsApi.getCampaignEvents(workspaceId, campaignId)
        : await calendarEventsApi.getWorkspaceEvents(workspaceId)

      if (response.error) {
        setError(response.error)
        showErrorUnless422('Failed to load calendar', response)
        setEvents([])
      } else {
        const calendarEvents = response.data || []
        setEvents(calendarEvents)
      }
    } catch (err) {
      console.error('Error in fetchEvents:', err)
      const message = err instanceof Error ? err.message : 'An unexpected error occurred'
      setError(message)
      showError('Error', message)
      setEvents([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, [workspaceId, campaignId, webId])

  return {
    events,
    setEvents,
    loading,
    error,
    refetch: fetchEvents,
  }
}
